import {
	PropsWithChildren,
	useEffect,
	useState,
	useRef,
	useMemo,
	useLayoutEffect,
	ReactElement,
	createContext,
	Context,
	useCallback,
	RefObject
} from "react";
import { useResizeObserver, useForceRerender, SelectionScopeKind } from "client/Hooks";
import { SizeInformation } from "client_legacy/ndaw/sizing";
import { Rect, Coordinate } from "client/Misc/commonTypes";
import styles from "./LassoContainer.module.less";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import classNames from "classnames";

export enum LassoMode {
	Override = 0,
	Append = 1
}

export type LassoContainerContextData = {
	hasLassoContainer: boolean;
	containerRect: Readonly<Rect>;
	lassoRect: Readonly<Rect> | undefined;
	lassoMode: LassoMode;
};

export const LassoContainerContext: Context<LassoContainerContextData> = createContext<LassoContainerContextData>({
	hasLassoContainer: false,
	containerRect: { left: 0, top: 0, right: 0, bottom: 0 },
	lassoRect: undefined,
	lassoMode: LassoMode.Override
});

export type LassoContainerProps = {
	isDisabled?: boolean;
	onLassoStart?(lassoMode: LassoMode): void;
	onLassoStop?(): void;
	height?: "fill" | "auto";
};

const emptyLassoContextData: LassoContainerContextData = {
	hasLassoContainer: false,
	containerRect: {left :0, top: 0, right: 0, bottom: 0},
	lassoRect: {left :0, top: 0, right: 0, bottom: 0},
	lassoMode: LassoMode.Append
};

export const LassoContainer = (props: PropsWithChildren<LassoContainerProps>): ReactElement => {
	const {
		isDisabled = false,
		onLassoStart,
		onLassoStop,
		children,
		height = "fill"
	} = props;

	const [lassoStart, setLassoStart] = useState<Coordinate | null>(null);
	const [lassoEnd, setLassoEnd] = useState<Coordinate | null>(null);
	const [lassoMode, setLassoMode] = useState<LassoMode>(LassoMode.Override);
	const [hasLasso, setHasLasso] = useState<boolean>(false);

	const [containerRect, setContainerRect] = useState<Rect>({left: 0, top: 0, right: 0, bottom: 0});
	const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const containerWidth: number = containerRect.right - containerRect.left;
	const containerHeight: number = containerRect.bottom - containerRect.top;

	const lassoRect = useMemo(
		(): Rect | undefined => {
			if (!lassoStart || !lassoEnd || !hasLasso) {
				return undefined;
			}

			return {
				left: Math.max(Math.min(lassoStart.x, lassoEnd.x), 0),
				top: Math.max(Math.min(lassoStart.y, lassoEnd.y), 0),
				right: Math.min(Math.max(lassoStart.x, lassoEnd.x), containerWidth),
				bottom: Math.min(Math.max(lassoStart.y, lassoEnd.y), containerHeight),
			};
		},
		[
			lassoStart,
			lassoEnd,
			hasLasso,
			containerWidth,
			containerHeight
		]
	);

	const containerSize: SizeInformation = useResizeObserver(containerRef);
	const [scrollHandler, scrollVersion] = useForceRerender();

	const updateContainerRectAndReturnImmediateResult = useCallback(
		(): Rect | undefined => {
			if (containerRef.current) {
				const screenRect: DOMRect = containerRef.current.getBoundingClientRect();

				const result: Rect = {
					left: screenRect.x,
					top: screenRect.y,
					right: screenRect.right,
					bottom: screenRect.bottom
				};
				setContainerRect(result);
				return result;
			}

			return undefined;
		},
		[]
	);

	useLayoutEffect(
		() => {
			hasImplicitDependencyOn(containerSize.width, containerSize.height, scrollVersion);
			updateContainerRectAndReturnImmediateResult();
		},
		[
			containerSize.width,
			containerSize.height,
			scrollVersion,
			updateContainerRectAndReturnImmediateResult
		]
	);

	const bodyContextData: LassoContainerContextData = useMemo(
		(): LassoContainerContextData => {
			if (lassoStart) {
				return {
					hasLassoContainer: true,
					containerRect,
					lassoRect,
					lassoMode
				};
			}
			return emptyLassoContextData;
		},
		[lassoStart, containerRect, lassoRect, lassoMode]
	);

	const mouseDownHandler = useMemo(
		(): ((mouseEvent: React.MouseEvent) => void) | undefined => {
			if (isDisabled) {
				return undefined;
			}

			return (mouseEvent: React.MouseEvent): void => {
				if (mouseEvent.button !== 0) {
					return;
				};

				mouseEvent.preventDefault();
				const updatedRect: Rect | undefined = updateContainerRectAndReturnImmediateResult();

				const x: number = mouseEvent.clientX - (updatedRect ? updatedRect.left : containerRect.left);
				const y: number = mouseEvent.clientY - (updatedRect ? updatedRect.top : containerRect.top);

				setLassoStart({ x, y });

				const lassoMode: LassoMode = mouseEvent.ctrlKey
					? LassoMode.Append
					: LassoMode.Override;
				setLassoMode(lassoMode);
			};
		},
		[
			isDisabled,
			containerRect.left,
			containerRect.top,
			updateContainerRectAndReturnImmediateResult
		]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			if (isDisabled) {
				return;
			}

			const mouseMoveHandler = (mouseEvent: MouseEvent): void => {
				if (!lassoStart) {
					return;
				}

				mouseEvent.preventDefault();

				const x: number = mouseEvent.clientX - containerRect.left;
				const y: number = mouseEvent.clientY - containerRect.top;

				if (hasLasso) {
					setLassoEnd({x, y});
					return;
				}

				const xDif: number = lassoStart.x - x;
				const yDif: number = lassoStart.y - y;

				const squaredDistance: number = xDif * xDif + yDif * yDif;
				if (squaredDistance < 9) {
					return;
				}

				if (onLassoStart) {
					onLassoStart(lassoMode);
				}
				setHasLasso(true);
				setLassoEnd({x, y});
			};

			const mouseUpHandler = (): void => {
				setHasLasso(false);
				setLassoStart(null);
				setLassoEnd(null);

				if (onLassoStop) {
					onLassoStop();
				}
			};

			document.addEventListener("mousemove", mouseMoveHandler);
			document.addEventListener("mouseup", mouseUpHandler);
			return () => {
				document.removeEventListener("mousemove", mouseMoveHandler);
				document.removeEventListener("mouseup", mouseUpHandler);
			};
		},
		[
			containerRect,
			lassoStart,
			hasLasso,
			lassoMode,
			onLassoStart,
			onLassoStop,
			isDisabled
		]
	);

	// Since container rect is calculated in screen coordinates, we need to monitor scroll events
	// To account for position changes relative to screen coordinates
	useEffect(
		() => {
			window.addEventListener("scroll", scrollHandler, true);
			return () => window.removeEventListener("scroll", scrollHandler, true);
		},
		[scrollHandler]
	);

	return (
		<LassoContainerContext.Provider value={bodyContextData}>
			<div
				className={classNames(
					styles.root,
					{
						[styles.root_fill]: height === "fill"
					}
				)}
				onMouseDown={mouseDownHandler}
				ref={containerRef}
			>
				{lassoRect && (
					<div
						className={styles.lasso}
						style={
							{
								left: lassoRect.left,
								top: lassoRect.top,
								width: lassoRect.right - lassoRect.left,
								height: lassoRect.bottom - lassoRect.top
							}
						}
					/>
				)}
				{children}
			</div>
		</LassoContainerContext.Provider>
	);
};

LassoContainer.displayName = "LassoContainer";

export function doOverlap(rect1: Rect, rect2: Rect): boolean {
	if (rect1.top >= rect2.bottom || rect2.top >= rect1.bottom) {
		return false;
	}

	if (rect1.left >= rect2.right || rect2.left >= rect1.right) {
		return false;
	}

	return true;
}

export const lassoModeToScopeKind: { [key in LassoMode]: SelectionScopeKind } = {
	[LassoMode.Override]: SelectionScopeKind.Override,
	[LassoMode.Append]: SelectionScopeKind.Append
};